import { useHandler } from "@redotech/react-util/hook";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { FulfillmentOrderLineItem } from "@redotech/redo-model/fulfillments/fulfillment-order-line-item";
import { SortDirection } from "@redotech/redo-views-common/table";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoBaseModal } from "@redotech/redo-web/arbiter-components/modal/redo-base-modal";
import { RedoModalHeader } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoTableTextCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { Flex } from "@redotech/redo-web/flex";
import { ColumnAlignment, TableFetcher } from "@redotech/redo-web/table";
import { Column2, Table2 } from "@redotech/redo-web/table2";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ItemNameCell } from "../fulfillment-groups/cells/item-name-cell";
import { getExpandedItems } from "../utils/outbound-labels-utils";

interface UnmergeModalProps {
  fulfillmentGroup: FulfillmentGroupData | null;
  isOpen: boolean;
  onClose: () => void;
  onUnmerge: (fulfillmentOrderIds: string[]) => Promise<void>;
}

interface FulfillmentOrderRow {
  fulfillmentOrderId: string;
  orderNumber: string;
  expandedItems: FulfillmentOrderLineItem[];
}

export const UnmergeModal = memo(function UnmergeModal({
  fulfillmentGroup,
  isOpen,
  onClose,
  onUnmerge,
}: UnmergeModalProps) {
  const fulfillmentOrders = useMemo(() => {
    if (!fulfillmentGroup || !fulfillmentGroup.orders) {
      return [];
    }

    return fulfillmentGroup.orders
      .filter((order) => order.fulfillmentOrderExternalId)
      .map((order): FulfillmentOrderRow => {
        const expandedItemsForThisOrder = getExpandedItems(
          fulfillmentGroup,
        ).filter(
          (item) =>
            item.fulfillmentOrderExternalId ===
            order.fulfillmentOrderExternalId,
        );
        return {
          fulfillmentOrderId: order.fulfillmentOrderExternalId,
          orderNumber: order.name,
          expandedItems: expandedItemsForThisOrder,
        };
      });
  }, [fulfillmentGroup]);

  const [selectedRows, setSelectedRows] = useState<Record<string, boolean>>({});

  const [loading, setLoading] = useState(false);

  const scrollAreaRef = useRef<HTMLDivElement>(null);

  const sortDefault = useMemo(
    () => ({ direction: SortDirection.ASC, key: "orderNumber" }),
    [],
  );

  useEffect(() => {
    if (isOpen && fulfillmentOrders.length > 0) {
      const initialSelections: Record<string, boolean> = {};
      for (const row of fulfillmentOrders) {
        initialSelections[row.fulfillmentOrderId] = true;
      }
      setSelectedRows(initialSelections);
    } else if (!isOpen) {
      setSelectedRows({});
    }
  }, [isOpen, fulfillmentOrders]);

  const handleCheckboxChange = useHandler(
    (fulfillmentOrderId: string, value: boolean) => {
      setSelectedRows((prev) => ({ ...prev, [fulfillmentOrderId]: value }));
    },
  );

  const toggleAllSelection = useHandler((selected: boolean) => {
    if (selected) {
      const allSelections: Record<string, boolean> = {};
      for (const row of fulfillmentOrders) {
        allSelections[row.fulfillmentOrderId] = true;
      }
      setSelectedRows(allSelections);
    } else {
      setSelectedRows({});
    }
  });

  const handleUnmerge = useHandler(async () => {
    const selectedFulfillmentOrderIds = Object.entries(selectedRows)
      .filter(([_, isSelected]) => isSelected)
      .map(([id]) => id);

    if (selectedFulfillmentOrderIds.length === 0) {
      return;
    }

    setLoading(true);
    try {
      await onUnmerge(selectedFulfillmentOrderIds);
      onClose();
    } finally {
      setLoading(false);
    }
  });

  const selectedCount = Object.values(selectedRows).filter(Boolean).length;
  const allChecked =
    selectedCount === fulfillmentOrders.length && fulfillmentOrders.length > 0;
  const someChecked = selectedCount > 0;
  const canUnmerge = selectedCount > 0;
  const buttonText = allChecked ? "Unmerge all" : "Unmerge selected";

  const fetcher: TableFetcher<FulfillmentOrderRow> = useMemo(
    () => ({
      loading: false,
      rows: fulfillmentOrders,
      totalRows: fulfillmentOrders.length,
      load: async () => {},
      counts: async () => ({}),
      async *data() {
        yield { items: fulfillmentOrders };
      },
    }),
    [fulfillmentOrders],
  );

  const columns: Column2<FulfillmentOrderRow>[] = [
    {
      alignment: ColumnAlignment.LEFT,
      key: "checkbox",
      title: (
        <Flex justify="center" p="md">
          <RedoCheckbox
            setValue={toggleAllSelection}
            size="sm"
            value={someChecked && !allChecked ? "indeterminate" : allChecked}
          />
        </Flex>
      ),
      renderNormalCell: ({ row }) => (
        <Flex justify="center" p="md">
          <RedoCheckbox
            onClick={(e) => e.stopPropagation()}
            setValue={(value) =>
              handleCheckboxChange(row.fulfillmentOrderId, value === true)
            }
            size="sm"
            value={selectedRows[row.fulfillmentOrderId] ?? false}
          />
        </Flex>
      ),
      width: 60,
      widthMode: { type: "fixed", width: "60px" },
      nonHidable: true,
    },
    {
      alignment: ColumnAlignment.LEFT,
      key: "orderNumber",
      title: "Order #",
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell text={row.orderNumber} />
      ),
      width: 150,
      widthMode: { type: "fill" },
    },
    {
      alignment: ColumnAlignment.LEFT,
      key: "items",
      title: "Items",
      renderNormalCell: ({ row }) => (
        <ItemNameCell lineItems={row.expandedItems} />
      ),
      width: 200,
      widthMode: { type: "fill" },
    },
  ];

  if (!fulfillmentGroup) {
    return null;
  }

  return (
    <RedoBaseModal isOpen={isOpen} onModalCloseRequested={onClose}>
      <Flex dir="column" gap="none" w="full">
        <RedoModalHeader
          cancelClicked={onClose}
          subtitle="Select which orders you want to unmerge from this shipment. Any orders that are NOT selected will remain merged."
          title="Unmerge shipments"
        />
        <div
          ref={scrollAreaRef}
          style={{ overflow: "auto", flex: 1, marginTop: "-32px" }}
        >
          <Table2
            columns={columns}
            fetcher={fetcher}
            onRowClick={() => {}}
            pageNumber={0}
            pageSize={fulfillmentOrders.length}
            scrollAreaRef={scrollAreaRef}
            setPageNumber={() => {}}
            setPageSize={() => {}}
            size="sm"
            sortDefault={sortDefault}
          />
        </div>
        <Flex
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth="1px"
          dir="row"
          gap="md"
          justify="space-between"
          p="3xl"
          w="full"
        >
          <RedoButton
            hierarchy="secondary"
            onClick={onClose}
            size="md"
            text="Cancel"
            theme="normal"
          />
          <RedoButton
            disabled={!canUnmerge || loading}
            hierarchy="primary"
            onClick={handleUnmerge}
            pending={loading}
            size="md"
            text={buttonText}
            theme="normal"
          />
        </Flex>
      </Flex>
    </RedoBaseModal>
  );
});
